// packages/chatbot/src/components/AgentSelector.tsx
import { useState, useRef, useEffect } from 'react';
import { Button } from '@dopomogai/ui';
import type { Agent } from '@dopomogai/agent-kit';
import { ChevronDown } from 'lucide-react';

interface AgentSelectorProps {
  agents: Agent[];
  activeAgentId: string;
  onSelectAgent: (agentId: string) => void;
}

export function AgentSelector({ agents, activeAgentId, onSelectAgent }: AgentSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const activeAgent = agents.find(a => a.id === activeAgentId);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [wrapperRef]);

  const handleSelect = (agentId: string) => {
    onSelectAgent(agentId);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <Button
        variant="ghost"
        text={activeAgent?.name || 'Select Agent'}
        onClick={() => setIsOpen(!isOpen)}
        icon={<ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />}
      />
      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-56 bg-white border-2 border-black rounded-lg shadow-offset-black-sm z-20 p-1">
          {agents.map(agent => (
            <button key={agent.id} onClick={() => handleSelect(agent.id)} className={`block w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${activeAgentId === agent.id ? 'bg-emerald-100 font-semibold' : 'hover:bg-slate-100'}`} >
              {agent.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}